import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FunctionComponent,
  type KeyboardEventHandler,
  type PropsWithChildren,
} from 'react';

import { useCallbackRef } from '@trello/dom-hooks';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import {
  activeAkPortals,
  ELEVATION_ATTR,
  getHighestVisibleElevation,
  LayerManagerPortal,
  Layers,
  useClickOutsideHandler,
  type Layer,
} from '@trello/layer-manager';

import { addActiveOverlay, removeActiveOverlay } from './overlayState';

import * as styles from './Overlay.module.less';

export type OverlayAlignment = 'bottom' | 'center' | 'top';
export type OverlayEffect = 'blur' | 'default';

interface OverlayProps {
  animation?: string;
  alignment?: OverlayAlignment;
  effect?: OverlayEffect;
  className?: string;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  isExiting?: boolean;
  layer?: Layer;
  onClose: () => void;
}

const noop = (): void => {};

export const Overlay: FunctionComponent<PropsWithChildren<OverlayProps>> = ({
  animation,
  alignment,
  effect = 'default',
  className = '',
  closeOnEscape,
  closeOnOutsideClick = true,
  isExiting = false,
  layer = Layers.Overlay,
  onClose = noop,
  children,
}) => {
  const [contentsElement, contentsRef] = useCallbackRef<HTMLDivElement>();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    addActiveOverlay(ref);
    return (): void => {
      removeActiveOverlay(ref);
    };
  }, []);

  const handleEscape = useCallback((): void => {
    // Certain Atlaskit component wire up their own escape handlers,
    // and they're set up as event listeners directly on the document, so we
    // should ignore keypresses when this is true.
    if (
      activeAkPortals.has('blanket') ||
      activeAkPortals.has('dialog') ||
      activeAkPortals.has('modal') ||
      activeAkPortals.has('spotlight')
    ) {
      return;
    }
    onClose();
  }, [onClose]);

  useShortcut(handleEscape, {
    enabled: closeOnEscape && !isExiting,
    key: Key.Escape,
    scope: Scope.Overlay,
  });

  // Set up an 'elevation aware' outside click handler
  // to detect clicks outside the content of the overlay
  const handleClickOutside = useCallback(
    (event: MouseEvent): void => {
      if (event.defaultPrevented) {
        return;
      }
      onClose();
    },
    [onClose],
  );

  useClickOutsideHandler({
    element: contentsElement,
    handleClickOutside,
    skip: isExiting || !closeOnOutsideClick,
  });

  const handleKeyboardEvent = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  // Taking advantage of lazy state initialization here, as we want to
  // calculate this value on first render and persist it for the entire life-
  // cycle
  const elevation = useMemo(() => getHighestVisibleElevation() + 1, []);

  useEffect(() => {
    document.body.classList.add('react-overlay-up');

    return (): void => {
      document.body.classList.remove('react-overlay-up');
    };
  }, []);

  return (
    <LayerManagerPortal layer={layer}>
      <div
        ref={ref}
        className={classNames(
          { [styles.alignTop]: alignment === 'top' },
          { [styles.alignCenter]: alignment === 'center' },
          { [styles.alignBottom]: alignment === 'bottom' },
          { [styles.effectBlur]: effect === 'blur' },
          styles.overlay,
          className,
        )}
        onKeyDownCapture={isExiting ? handleKeyboardEvent : undefined}
        onKeyPressCapture={isExiting ? handleKeyboardEvent : undefined}
        style={{ animation }}
      >
        <div
          // Used in desktop to position overlay contents with respect to the Windows menu
          id="overlay-contents"
          ref={contentsRef}
          className={styles.contents}
          style={{ pointerEvents: isExiting ? 'none' : undefined }}
          {...{ [ELEVATION_ATTR]: elevation }}
        >
          {children}
        </div>
      </div>
    </LayerManagerPortal>
  );
};
